//Mar25 TextEditor:
// Enterprise wrapper migration:
// - Keep the existing TextEditor public API unchanged for HeaderSettings.
// - Delegate contentEditable behavior to RichTextEditorBase.
// - Preserve existing onChange payload shape: { text, styles }.
// - Keep toolbarStyles support that HeaderSettings already passes.
// - Do not write to FieldEditContext, FieldMetadataContext, Firestore, or settings here.

import React, { useCallback } from 'react';
import RichTextEditorBase from './RichTextEditorBase';

const TextEditor = ({
  value,
  onChange,
  placeholder,
  styles,
  toolbarStyles,
  onBlur,
  onFocus,
  disabled = false,
  readOnly = false,
}) => {
  const handleEditorChange = useCallback(
    (payload) => {
      if (!onChange) return;

      // Preserve the legacy TextEditor API expected by HeaderSettings:
      // HeaderSettings.handleChange currently expects { text, styles }.
      //
      // Enterprise editor contract:
      // Pass through intent metadata so settings panels can distinguish
      // ordinary typing/blur commits from intentional toolbar style changes.
      onChange({
        text: payload.text,
        styles: payload.styles,

        // Extended editor payload fields
        html: payload.html,
        plainText: payload.plainText,
        isEmpty: payload.isEmpty,
        changeSource: payload.changeSource,
        changedStyleKey: payload.changedStyleKey,
        changedStyleValue: payload.changedStyleValue,
        affectsRootStyle: payload.affectsRootStyle,
      });
    },
    [onChange]
  );

  const handleEditorCommit = useCallback(
    (payload) => {
      // Do not auto-persist here.
      // HeaderSettings will own commit timing in the next step.
      // This wrapper keeps compatibility with the old TextEditor behavior,
      // where onChange was the only integration point.
      if (!onChange) return;

      onChange({
        text: payload.text,
        styles: payload.styles,

        // Extended editor payload fields
        html: payload.html,
        plainText: payload.plainText,
        isEmpty: payload.isEmpty,
        changeSource: payload.changeSource,
        changedStyleKey: payload.changedStyleKey,
        changedStyleValue: payload.changedStyleValue,
        affectsRootStyle: payload.affectsRootStyle,
      });
    },
    [onChange]
  );

  return (
    <RichTextEditorBase
      value={value || ''}
      styles={styles || {}}
      placeholder={placeholder}
      onChange={handleEditorChange}
      onCommit={handleEditorCommit}
      commitOnBlur={false}
      toolbarSx={toolbarStyles}
      minHeight={100}
      disabled={disabled}
      readOnly={readOnly}
      onBlur={onBlur}
      onFocus={onFocus}
      normalizeLineBreaks
      preservePastedFormatting={false}
    />
  );
};

export default TextEditor;